/**
 * Frontend-safe standard combat action rules.
 *
 * The repo also has a shared TypeScript copy under /shared, but the CRA/Vercel
 * frontend build cannot import files from outside src/. Keep this module in
 * sync with the shared rules contract until the frontend build boundary is
 * widened intentionally.
 * date_of_change: 2026-08-21
 */

export const defaultStandardActionRules = Object.freeze({
  dash: {
    key: 'dash',
    name: 'Dash',
    description: 'Gain extra movement equal to your Speed for the current turn.',
    actionType: 'action',
    activationType: 'action',
    targetRequirement: 'none',
    executionRoute: 'standard',
    ruleReference: 'Actions in Combat: Dash',
    effectTags: ['movement', 'self', 'until-end-of-turn']
  },
  disengage: {
    key: 'disengage',
    name: 'Disengage',
    description: 'Your movement does not provoke Opportunity Attacks for the rest of the current turn.',
    actionType: 'action',
    activationType: 'action',
    targetRequirement: 'none',
    executionRoute: 'standard',
    ruleReference: 'Actions in Combat: Disengage',
    effectTags: ['movement', 'self', 'opportunity-attacks', 'until-end-of-turn']
  },
  dodge: {
    key: 'dodge',
    name: 'Dodge',
    description: 'Until the start of your next turn, attacks against you have disadvantage if you can see the attacker, and you have advantage on Dexterity saving throws.',
    actionType: 'action',
    activationType: 'action',
    targetRequirement: 'none',
    executionRoute: 'standard',
    ruleReference: 'Actions in Combat: Dodge',
    effectTags: ['defense', 'self', 'dexterity-saves', 'until-start-of-next-turn']
  },
  help: {
    key: 'help',
    name: 'Help',
    description: 'Aid a creature with an ability check or grant attack assistance within 5 feet, depending on the declared help mode.',
    actionType: 'action',
    activationType: 'action',
    targetRequirement: 'creature',
    executionRoute: 'standard',
    rangeFeet: 5,
    ruleReference: 'Actions in Combat: Help',
    effectTags: ['ally', 'advantage', 'declared-mode']
  },
  hide: {
    key: 'hide',
    name: 'Hide',
    description: 'While heavily obscured or behind three-quarters or total cover and outside every enemy line of sight, make a DC 15 Dexterity (Stealth) check to gain the Invisible condition while hidden.',
    actionType: 'action',
    activationType: 'action',
    targetRequirement: 'none',
    executionRoute: 'standard',
    ruleReference: 'D&D Free Rules (2024): Hide [Action]',
    effectTags: ['stealth', 'self', 'dc-15', 'invisible', 'declared-context']
  },
  ready: {
    key: 'ready',
    name: 'Ready',
    description: 'Choose a perceivable trigger and ready an action or movement to use as your reaction.',
    actionType: 'action',
    activationType: 'action',
    targetRequirement: 'trigger',
    executionRoute: 'standard',
    ruleReference: 'Actions in Combat: Ready',
    effectTags: ['reaction', 'triggered', 'self']
  },
  search: {
    key: 'search',
    name: 'Search',
    description: 'Make a Wisdom (Perception) or Intelligence (Investigation) check to notice or examine something.',
    actionType: 'action',
    activationType: 'action',
    targetRequirement: 'none',
    executionRoute: 'standard',
    ruleReference: 'Actions in Combat: Search',
    effectTags: ['skill-check', 'perception', 'investigation']
  },
  'use-object': {
    key: 'use-object',
    name: 'Use an Object',
    description: 'Interact with an object that requires your action.',
    actionType: 'action',
    activationType: 'action',
    targetRequirement: 'object',
    executionRoute: 'standard',
    rangeFeet: 5,
    ruleReference: 'Actions in Combat: Use an Object',
    effectTags: ['object-interaction']
  },
  move: {
    key: 'move',
    name: 'Move',
    description: 'Move up to your Speed.',
    actionType: 'movement',
    activationType: 'movement',
    targetRequirement: 'none',
    executionRoute: 'standard',
    ruleReference: 'Movement and Position',
    effectTags: ['movement']
  },
  'bonus-action-dash': {
    key: 'bonus-action-dash',
    name: 'Dash',
    description: 'Gain extra movement equal to your Speed as a bonus action when another rule allows it.',
    actionType: 'bonus',
    activationType: 'bonus action',
    targetRequirement: 'none',
    executionRoute: 'standard',
    ruleReference: 'Actions in Combat: Dash',
    effectTags: ['movement', 'bonus-action', 'self', 'until-end-of-turn']
  },
  'bonus-action-disengage': {
    key: 'bonus-action-disengage',
    name: 'Disengage',
    description: 'Your movement does not provoke Opportunity Attacks this turn when a feature lets you Disengage as a bonus action.',
    actionType: 'bonus',
    activationType: 'bonus action',
    targetRequirement: 'none',
    executionRoute: 'standard',
    ruleReference: 'Rogue: Cunning Action',
    effectTags: ['movement', 'bonus-action', 'self', 'opportunity-attacks', 'until-end-of-turn']
  },
  'bonus-action-hide': {
    key: 'bonus-action-hide',
    name: 'Hide',
    description: 'When a feature allows it, attempt the 2024 Hide action as a bonus action after declaring qualifying concealment or cover and no enemy line of sight.',
    actionType: 'bonus',
    activationType: 'bonus action',
    targetRequirement: 'none',
    executionRoute: 'standard',
    ruleReference: 'Rogue: Cunning Action; D&D Free Rules (2024): Hide [Action]',
    effectTags: ['stealth', 'bonus-action', 'self', 'dc-15', 'invisible', 'declared-context']
  },
  speak: {
    key: 'speak',
    name: 'Speak',
    description: 'Speak briefly during your turn.',
    actionType: 'free',
    activationType: 'free',
    targetRequirement: 'none',
    executionRoute: 'standard',
    ruleReference: 'Other Activity on Your Turn',
    effectTags: ['free-action']
  },
  'drop-item': {
    key: 'drop-item',
    name: 'Drop Item',
    description: 'Drop something you are holding without using your action.',
    actionType: 'free',
    activationType: 'free',
    targetRequirement: 'none',
    executionRoute: 'standard',
    ruleReference: 'Other Activity on Your Turn',
    effectTags: ['free-action', 'item']
  },
  'draw-weapon': {
    key: 'draw-weapon',
    name: 'Draw Weapon',
    description: 'Draw or sheathe a weapon as your free object interaction.',
    actionType: 'object',
    activationType: 'free object',
    targetRequirement: 'object',
    executionRoute: 'standard',
    ruleReference: 'Other Activity on Your Turn',
    effectTags: ['free-object', 'item']
  }
});

/**
 * Resolve only an exact canonical key. Display names are intentionally not an
 * execution API and must never be parsed to recover mechanics.
 * date_of_change: 2026-08-21
 */
export const getStandardActionRule = (key = null) => (
  key && Object.prototype.hasOwnProperty.call(defaultStandardActionRules, key)
    ? defaultStandardActionRules[key]
    : null
);

export const buildStandardActionCatalogEntries = (speed) => {
  if (!Number.isFinite(speed) || speed < 0) {
    throw new Error('combatant.speed must be a non-negative number before standard actions can be built.');
  }
  const normalizedSpeed = speed;
  const standardRules = [
    defaultStandardActionRules.dash,
    defaultStandardActionRules.disengage,
    defaultStandardActionRules.dodge,
    defaultStandardActionRules.help,
    defaultStandardActionRules.hide,
    defaultStandardActionRules.ready,
    defaultStandardActionRules.search,
    defaultStandardActionRules['use-object'],
    defaultStandardActionRules.move,
    defaultStandardActionRules.speak,
    defaultStandardActionRules['drop-item'],
    defaultStandardActionRules['draw-weapon']
  ];

  return standardRules.map((rule) => {
    if (rule.key === 'move') {
      return {
        standardActionKey: rule.key,
        type: 'move',
        name: `Move ${normalizedSpeed}ft`,
        description: rule.description,
        uiType: 'movement',
        activationType: rule.activationType,
        actionType: 'movement',
        executionRoute: rule.executionRoute,
        targetRequirement: rule.targetRequirement,
        ...(rule.rangeFeet !== undefined ? { rangeFeet: rule.rangeFeet } : {}),
        distance: normalizedSpeed,
        movementCost: normalizedSpeed,
        terrain: 'normal',
        ruleReference: rule.ruleReference,
        effectTags: rule.effectTags.slice()
      };
    }

    return {
      standardActionKey: rule.key,
      type: rule.actionType,
      name: rule.name,
      description: rule.description,
      uiType: rule.actionType,
      activationType: rule.activationType,
      actionType: rule.activationType,
      executionRoute: rule.executionRoute,
      targetRequirement: rule.targetRequirement,
      ...(rule.rangeFeet !== undefined ? { rangeFeet: rule.rangeFeet } : {}),
      ruleReference: rule.ruleReference,
      effectTags: rule.effectTags.slice()
    };
  });
};

const StandardActionRules = {
  defaultStandardActionRules,
  getStandardActionRule,
  buildStandardActionCatalogEntries
};

export default StandardActionRules;
